import { FetchData } from '../cloneme-classes';
import { UserManager } from '../cloneme-classes';
import { retargetSubscribeLinks } from '../utils';

const ANON_MAX_MSGS = 5;
const ANON_BROWSER_ID_KEY = 'flirtive_anon_browser_id';
const ANON_USED_KEY = 'flirtive_anon_msgs_used';
const ANON_SESSION_KEY_PREFIX = 'flirtive_anon_session_';

function getOrCreateBrowserId(): string {
  let id = localStorage.getItem(ANON_BROWSER_ID_KEY);
  if (!id) {
    id = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
      ? (crypto as any).randomUUID()
      : `anon-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(ANON_BROWSER_ID_KEY, id);
  }
  return id;
}

function getAnonUsed(): number {
  const v = parseInt(localStorage.getItem(ANON_USED_KEY) || '0', 10);
  return isNaN(v) ? 0 : v;
}

function setAnonUsed(n: number): void {
  localStorage.setItem(ANON_USED_KEY, String(n));
}

export class ChatPage {
  private userManager: UserManager | undefined;
  private introVideoPlayedKey: string;
  private talkVideo: HTMLVideoElement | null;
  private talkVideoIdle: HTMLVideoElement | null;
  private cloneImage: HTMLImageElement | null;
  private chatIframe: HTMLIFrameElement | null = null;
  private isAnonSession: boolean = false;
  private iframeRevealed: boolean = false;

  // Reserve a fixed aspect-ratio box for the companion avatar so the page doesn't
  // shift when the (varying aspect-ratio) cover image / Vimeo video loads.
  private injectLayoutFixes(): void {
    if (document.getElementById('cc-avatar-layout-fix')) return;
    const style = document.createElement('style');
    style.id = 'cc-avatar-layout-fix';
    style.textContent = `
      .videos-container { aspect-ratio: 3 / 4; height: auto; }
      .videos-container > .modal_generic-image-copy,
      .videos-container > .video-intro-basel,
      .videos-container .generic_modal-video_holder,
      .videos-container video {
        position: absolute; inset: 0;
        width: 100%; height: 100%;
        object-fit: cover;
      }
    `;
    document.head.appendChild(style);
  }

  public async init(): Promise<void> {
    this.injectLayoutFixes();
    this.userManager = await UserManager.getInstance();

    if (this.userManager.status == UserManager.AUTH_STATUS__LOGGED_OUT) {
      document.getElementById('cc-chat-anti-flash')?.remove();
      if (document.querySelector('.pricing-col-starter .logged-out')) {
        // @ts-ignore
        document.querySelector('.pricing-col-starter .logged-out').style.display = 'block';
      }

      if (document.querySelector('.pricing-col-starter .pricing-content-pricing')) {
        // @ts-ignore
        document.querySelector('.pricing-col-starter .pricing-content-pricing').style.display =
          'none';
      }
    }
    await this.initialize();
    this.listenForIframeMessages();
  }

  private listenForIframeMessages(): void {
    window.addEventListener('message', (event: MessageEvent) => {
      if (typeof event.data !== 'string') return;
      try {
        const data = JSON.parse(event.data);
        if (data.action === 'panel::ready') {
          this.revealIframe();
          return;
        }
        if (data.action === 'scroll::toGallery') {
          this.scrollToGallery();
          return;
        }
        if (data.action === 'share::companion') {
          const parsed = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
          void this.shareCompanion(parsed?.name);
          return;
        }
        if (data.action === 'anon::userSent') {
          this.handleAnonUserSent();
          return;
        }
        if (data.action !== 'update::errors') return;
        const parsed = typeof data.data === 'string' ? JSON.parse(data.data) : data.data;
        const code = parsed?.error;
        // Subscription is the primary recovery path for the chat paywall — route
        // everyone who hits it to Subscribe (tokens are not gating chat access).
        if (code === 'trial_exceeded' || code === 'subscription_lapsed' || code === 'tokens_exhausted') {
          this.showSubscribeNowPopup();
        }
      } catch (e) {
        console.warn('[ChatPage] iframe message parse failed', e);
      }
    });
  }

  // Keep the Webflow video cover up until the iframe panel can paint, then fade
  // it out. Avoids flashing the iframe's loading skeleton / black video frame.
  private revealIframe(): void {
    if (this.iframeRevealed) return;
    this.iframeRevealed = true;
    const videosContainer = document.querySelector('.videos-container') as HTMLElement | null;
    if (!videosContainer) return;
    videosContainer.style.transition = 'opacity 0.4s ease';
    videosContainer.style.opacity = '0';
    videosContainer.style.pointerEvents = 'none';
  }

  private async shareCompanion(name?: string): Promise<void> {
    const url = window.location.href.split('#')[0];
    const title = name ? `Chat with ${name} on Flirtive` : 'Chat on Flirtive';
    const text = name ? `Meet ${name} on Flirtive` : 'Check out Flirtive';
    const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void> };
    try {
      if (nav.share) {
        await nav.share({ title, text, url });
        return;
      }
    } catch (e: any) {
      if (e?.name === 'AbortError') return;
    }
    try {
      await navigator.clipboard.writeText(url);
      this.showCopiedToast();
    } catch {
      window.prompt('Copy this link:', url);
    }
  }

  private showCopiedToast(): void {
    const existing = document.getElementById('cc-share-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.id = 'cc-share-toast';
    toast.textContent = 'Link copied!';
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      background: '#ff3d8a',
      color: '#fff',
      padding: '10px 18px',
      borderRadius: '999px',
      fontSize: '14px',
      fontFamily: 'system-ui, sans-serif',
      zIndex: '99999',
      boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
      opacity: '0',
      transition: 'opacity 200ms ease',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(toast);
    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });
    setTimeout(() => {
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 250);
    }, 2000);
  }

  private scrollToGallery(): void {
    const existingGalleryLink = document.querySelector('a.view-gallery-txt-button[href^="#"]') as HTMLAnchorElement | null;
    const linkTargetSelector = existingGalleryLink?.getAttribute('href');
    const target =
      document.getElementById('image-gallery') ||
      document.getElementById('image-gallery-placeholder') ||
      document.getElementById('Gallery') ||
      (linkTargetSelector ? document.querySelector(linkTargetSelector) : null);
    if (target) (target as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  public showSubscribeNowPopup() {
    const subscribeNowPopup = document.querySelector('[cc-element="subscribe-now-modal"]') as HTMLElement | null;
    if (!subscribeNowPopup) return;
    retargetSubscribeLinks(subscribeNowPopup);
    subscribeNowPopup.classList.remove('hide');
    subscribeNowPopup.style.opacity = '1';
    subscribeNowPopup.style.visibility = 'visible';
    subscribeNowPopup.style.display = 'flex';
    this.wireSubscribeNowClose(subscribeNowPopup);
  }

  private hideSubscribeNowPopup(): void {
    const subscribeNowPopup = document.querySelector('[cc-element="subscribe-now-modal"]') as HTMLElement | null;
    if (!subscribeNowPopup) return;
    subscribeNowPopup.style.opacity = '0';
    subscribeNowPopup.style.visibility = 'hidden';
    subscribeNowPopup.style.display = 'none';
    subscribeNowPopup.classList.add('hide');
  }

  private wireSubscribeNowClose(modal: HTMLElement): void {
    const dataKey = 'ccCloseBound';
    const closeBtns = modal.querySelectorAll<HTMLElement>(
      '[cc-element="close-subscribe-now-modal"], [cc-element="close-modal"]'
    );
    closeBtns.forEach((btn) => {
      if (btn.dataset[dataKey] === '1') return;
      btn.dataset[dataKey] = '1';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hideSubscribeNowPopup();
      });
    });
    if (!(modal as any)[`__${dataKey}`]) {
      (modal as any)[`__${dataKey}`] = true;
      modal.addEventListener('click', (e) => {
        if (e.target === modal) this.hideSubscribeNowPopup();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && modal.style.display !== 'none') {
          this.hideSubscribeNowPopup();
        }
      });
    }
  }

  public showBuyTokensPopup() {
    const btn = document.querySelector('[cc-element="PurchaseTokensPopUpButton"]') as HTMLAnchorElement | null;
    btn?.click();
  }

  private async initialize(): Promise<void> {
    this.cloneImage = document.querySelector('[cc-element="clone-image"]') as HTMLImageElement; // Replace with your container's selector
    this.talkVideo = document.querySelector('[cc-element="video-stream"]') as HTMLVideoElement; // Replace with your container's selector

    this.talkVideoIdle = document.querySelector(
        '[cc-element="video-stream-idle"]'
    ) as HTMLVideoElement; // Replace with your container's selector

    this.talkVideo?.setAttribute('playsinline', '');
    this.talkVideoIdle?.setAttribute('playsinline', '');
    // Show the Webflow idle-video cover (with its volume toggle) on the profile.
    if (this.talkVideoIdle) this.cloneImage.style.opacity = '0';
    else this.cloneImage.style.opacity = '1';
    // Hide the legacy Lottie loader to avoid it flickering during load.
    const loadingSpin = document.querySelector('[cc-element="chat-loading-spin"]') as HTMLElement | null;
    if (loadingSpin) loadingSpin.style.display = 'none';

    const fetchImageGallery: boolean =
        (document.querySelector('body')?.dataset?.fetchImageGallery || '') === 'true';
    if (fetchImageGallery) {
      const imageGalleryContainer = document.getElementById('image-gallery') as HTMLElement;
      if (imageGalleryContainer) imageGalleryContainer.classList.remove('hide');
      this.getImageGallery(this.getSlug());
    } else {
      const imageGalleryPlaceholderContainer = document.getElementById(
          'image-gallery-placeholder'
      ) as HTMLElement;
      if (imageGalleryPlaceholderContainer)
        imageGalleryPlaceholderContainer.classList.remove('hide');
    }

    this.setupChatButton();
  }

  public setupChatButton() {
    const chatWithMeButton = document.getElementById('chat-with-me-button');
    const loggedIn = this.userManager?.status !== UserManager.AUTH_STATUS__LOGGED_OUT;
    const cloneId = this.getCloneId();

    chatWithMeButton?.addEventListener('click', () => {
      if (loggedIn) {
        void this.startChat(cloneId);
      } else {
        void this.startAnonChat(cloneId);
      }
    });
  }

  public async startAnonChat(cloneId: string): Promise<void> {
    if (!cloneId) return;
    this.isAnonSession = true;
    const browserId = getOrCreateBrowserId();

    const profileContent = document.querySelector('[cc-element="profile-content"]') as HTMLElement;
    profileContent?.style.setProperty('display', 'none');
    const chatWithMeButton = document.getElementById('chat-with-me-button');
    chatWithMeButton?.style.setProperty('display', 'none');

    const cacheKey = `${ANON_SESSION_KEY_PREFIX}${cloneId}_${browserId}`;
    let iframeUrl: string | null = localStorage.getItem(cacheKey);
    if (!iframeUrl) {
      try {
        const data = await FetchData.createAnonChatSession(cloneId, browserId);
        if (data?.url) {
          iframeUrl = data.url as string;
          localStorage.setItem(cacheKey, iframeUrl);
        }
      } catch (e) {
        console.warn('[anon-chat] createAnonChatSession failed', e);
      }
    }
    if (!iframeUrl) return;

    const iframeContainer = document.querySelector('[cc-element="chat_iframe_container"]') as HTMLElement;
    iframeContainer.classList.add('chat_iframe_container_active');
    iframeContainer.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.position = 'absolute';
    iframe.style.display = 'block';
    iframe.style.paddingRight = '40px';
    iframe.style.paddingBottom = '20px';
    iframe.setAttribute('allow', 'microphone; autoplay');
    iframe.src = `${iframeUrl}&anon=1`;
    iframe.addEventListener('load', () => {
      this.postGalleryToIframe(iframe);
      this.postAnonQuotaToIframe(iframe);
      window.setTimeout(() => this.revealIframe(), 4000);
    });
    iframeContainer.appendChild(iframe);
    this.chatIframe = iframe;
  }

  private postAnonQuotaToIframe(iframe: HTMLIFrameElement): void {
    const payload = JSON.stringify({
      action: 'anon::quota',
      data: JSON.stringify({ used: getAnonUsed(), max: ANON_MAX_MSGS }),
    });
    iframe.contentWindow?.postMessage(payload, '*');
  }

  private handleAnonUserSent(): void {
    const used = Math.min(ANON_MAX_MSGS, getAnonUsed() + 1);
    setAnonUsed(used);
    if (this.chatIframe) this.postAnonQuotaToIframe(this.chatIframe);
  }

  public getImageGallery(slug: any): void {
    const imageGalleryContainer = document.getElementById('image-gallery') as HTMLElement;

    try {
      FetchData.fetchImageGallery(slug)
          .then((response) => {
            console.log('response', response);
            const skeletonsContainers = imageGalleryContainer.querySelectorAll('[ms-code-skeleton]');
            if (skeletonsContainers) skeletonsContainers.forEach((skeleton) => skeleton.remove());
            // response.data.attributes.premiumImageGallery.data
            // .filter((elem: any, index: number) => index < 9)
            response.forEach((imageData: any) => {
              const imageDiv = document.createElement('div');
              // imageDiv.className = 'full-width';
              const image = document.createElement('img');
              image.src = imageData.attributes.url;
              image.className = 'max-width-full';
              imageDiv.appendChild(image);
              imageGalleryContainer.appendChild(imageDiv);
            });
            // if (this.imageLibrary) {
            //   this.showImageLibrary();
            //   this.notify.showLoadingDone('Uploading Done');
            // }
          })
          .catch((error) => {
            // this.notify.show(error);
            console.error('There was a problem with the fetch operation:', error.message);
          });
    } catch (error) {
      console.error('There was a problem with the fetch operation:', (error as Error).message);
      throw new Error((error as Error).message || 'There was a problem with the fetch operation');
    }
  }

  public async startChat(cloneId:string): Promise<void> {
    const profileContent = document.querySelector('[cc-element="profile-content"]') as HTMLElement;
    profileContent?.style.setProperty('display', 'none');
    const chatWithMeButton = document.getElementById('chat-with-me-button');
    chatWithMeButton?.style.setProperty('display', 'none');

    const iframeData = await FetchData.createChatSession(cloneId);
    const iframeContainer = document.querySelector('[cc-element="chat_iframe_container"]') as HTMLElement;
    iframeContainer.classList.add('chat_iframe_container_active');
    iframeContainer.innerHTML = '';
    const iframe = document.createElement('iframe');
    iframe.style.width = '100%';
    iframe.style.height = '100%';
    iframe.style.position = 'absolute';
    iframe.style.display = 'block';
    iframe.style.paddingRight = '40px';
    iframe.style.paddingBottom = '20px';
    iframe.setAttribute('allow', 'microphone; autoplay');
    iframe.src = `${iframeData.url}&jwt=${localStorage.getItem('jwt')}`;
    iframe.addEventListener('load', () => {
      this.postGalleryToIframe(iframe);
      window.setTimeout(() => this.revealIframe(), 4000);
    });
    iframeContainer.appendChild(iframe);
    this.chatIframe = iframe;
  }

  private collectGalleryImages(): { url: string; alt?: string }[] {
    const selectors = [
      '#Gallery img.lightbox-thumb',
      '#Gallery .lightbox-thumb-wrapper img',
      '#Gallery img',
      '#image-gallery img',
    ];
    const found: HTMLImageElement[] = [];
    for (const sel of selectors) {
      document.querySelectorAll<HTMLImageElement>(sel).forEach((img) => {
        if (!found.includes(img)) found.push(img);
      });
      if (found.length > 0) break;
    }
    const seen = new Set<string>();
    const items: { url: string; alt?: string }[] = [];
    for (const img of found) {
      const url = img.currentSrc || img.src || '';
      if (!url || seen.has(url)) continue;
      seen.add(url);
      items.push({ url, alt: img.alt || undefined });
    }
    return items;
  }

  private postGalleryToIframe(iframe: HTMLIFrameElement, attempt = 0): void {
    const items = this.collectGalleryImages();
    if (items.length === 0 && attempt < 10) {
      // Webflow CMS / lazy images may still be hydrating
      window.setTimeout(() => this.postGalleryToIframe(iframe, attempt + 1), 500);
      return;
    }
    if (items.length === 0) return;
    const payload = JSON.stringify({ action: 'gallery::images', data: JSON.stringify({ items }) });
    iframe.contentWindow?.postMessage(payload, '*');
  }

  private getSlug(): string {
    const slug: string = document.querySelector('body')?.dataset?.twinSlug || '';
    const slugId: string = document.querySelector('body')?.dataset?.twinSlugId || '';
    return slugId || slug;
  }

  private getCloneId(): string {
    const slug: string = document.querySelector('body')?.dataset?.twinSlug || '';
    const uuid: string = document.querySelector('body')?.dataset?.twinSlugUuid || '';
    const slugId: string = document.querySelector('body')?.dataset?.twinSlugId || '';
    return uuid || slugId || slug;
  }
  private shouldPlayIntroVideo(): boolean {
    if (UserManager.AUTH_STATUS__LOGGED_OUT) {
      return false;
    }
    return !localStorage.getItem(this.introVideoPlayedKey);
  }

  private markIntroVideoAsPlayed(): void {
    localStorage.setItem(this.introVideoPlayedKey, 'true');
  }
}
